import React, { useEffect, useCallback } from 'react';

import {
  checkInQuery,
  CheckInForm,
  checkInService,
  guestProfileQuery,
  guestProfileService,
} from '@lib/state';
import {
  CheckInConfirmation,
  CheckInError,
  useObservable,
  useFormEvents,
  useReservation,
  useCheckInRequirements,
  ReservationRouteProps,
  BillingFields,
  CheckInRequirements,
  LoadingIndicator,
  getAcceptCardData,
  ResponsiveContainer,
  webStyles,
} from '@lib/common';

export const CheckInPage: React.FC<ReservationRouteProps> = ({ match, history }) => {
  const [{ error, status }] = useFormEvents(CheckInForm.CheckIn);

  const requirements = useCheckInRequirements(match.params['reservationId']);
  const reservation = useReservation(match.params['reservationId']);
  const stay = useObservable(checkInQuery.guestStay, 'async');
  const guest = useObservable(guestProfileQuery.guest);
  const styles = webStyles();

  useEffect(() => {
    guestProfileService.getGuestProfile();
  }, []);

  useEffect(() => {
    if (reservation && requirements?.length === 0) {
      checkInService.checkInReservation(reservation);
    }
    // if the only dependency is reservation,
    // the call to check-in happens 4 times
    // eslint-disable-next-line
  }, [reservation?.id, requirements]);

  useEffect(() => () => checkInService.reset(), []);

  const onSubmit = useCallback(
    ({ paymentProfileId, billing, card, savePaymentMethod }: BillingFields) => {
      if (reservation) {
        checkInService.checkInReservation(
          reservation,
          billing,
          savePaymentMethod,
          paymentProfileId,
          getAcceptCardData(card)
        );
      }
    },
    [reservation]
  );

  if (!reservation || !guest) return <LoadingIndicator fillContainer />;

  if (error) return <CheckInError error={error} />;

  if (stay && stay.rooms.length > 0) {
    return (
      <ResponsiveContainer data-testid="guestCheckIn">
        <CheckInConfirmation
          {...stay}
          finishRoute="/profile/stays"
          contentClass={styles.listContentBlock}
          sectionVariant="contained"
        />
      </ResponsiveContainer>
    );
  }

  if (requirements?.length) {
    return (
      <ResponsiveContainer data-testid="guestCheckIn">
        <CheckInRequirements
          requirements={requirements}
          reservation={reservation}
          status={status}
          guest={guest}
          onSubmit={onSubmit}
          onCancel={() => {
            checkInService.reset();
            history.push('/profile/stays');
          }}
        />
      </ResponsiveContainer>
    );
  }

  return <LoadingIndicator loadingText="Checking In..." />;
};
